import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge, Box, Button, Flex, Heading, Icon, Spinner, Text, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  FaCalendarAlt, FaEnvelope, FaFileAlt, FaMapMarkerAlt, FaTrash,
} from 'react-icons/fa';
import { supabase } from '../../lib/supabaseClient';
import { formatDate, pickLocalized } from '../../lib/content';
import { APPLICATIONS_BUCKET, createSignedUrl, removeStoredFile } from './storage';

const DocumentButton = ({ url, label, onError }) => {
  const { t } = useTranslation();

  if (!url) return null;

  const open = async () => {
    try {
      const signedUrl = await createSignedUrl(url, APPLICATIONS_BUCKET);
      if (signedUrl) window.open(signedUrl, '_blank', 'noopener');
    } catch (error) {
      onError(error);
    }
  };

  return (
    <Button size="xs" variant="outline" colorScheme="brand" onClick={open}>
      <Icon as={FaFileAlt} mr={1} />
      {label || t('admin.viewDocument')}
    </Button>
  );
};

const ApplicationsPanel = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('applications')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      toast({
        title: `${t('admin.errorPrefix')}: ${error.message}`,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
    }
    setApplications(data || []);
    setLoading(false);
  }, [t, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleError = (error) => {
    toast({
      title: error.message,
      status: 'error',
      duration: 4000,
      isClosable: true,
      position: 'top',
    });
  };

  const handleDelete = async (application) => {
    if (!window.confirm(t('admin.confirmDelete'))) return;
    setDeletingId(application.id);
    const { error } = await supabase.from('applications').delete().eq('id', application.id);
    if (error) {
      handleError(error);
      setDeletingId(null);
      return;
    }
    await Promise.all([
      removeStoredFile(application.cv_url, APPLICATIONS_BUCKET),
      removeStoredFile(application.cover_letter_url, APPLICATIONS_BUCKET),
      removeStoredFile(application.other_documents_url, APPLICATIONS_BUCKET),
    ]);
    toast({
      title: t('admin.deleted'),
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
    setDeletingId(null);
    load();
  };

  return (
    <Box>
      {loading ? (
        <Flex justify="center" py={12}>
          <Spinner size="lg" color="brand.500" />
        </Flex>
      ) : applications.length === 0 ? (
        <Text color="text.muted" textAlign="center" py={10}>
          {t('admin.noApplications')}
        </Text>
      ) : (
        applications.map((application) => (
          <Flex
            key={application.id}
            bg="surface.base"
            border="1px solid"
            borderColor="border.subtle"
            borderRadius="xl"
            p={4}
            mb={3}
            align="center"
            gap={4}
            boxShadow="sm"
            flexWrap="wrap"
          >
            <Box flex="1" minW="220px">
              <Heading as="h4" size="sm" mb={1} noOfLines={1}>
                {application.full_name}
              </Heading>
              <Flex gap={2} flexWrap="wrap">
                {application.email && (
                  <Badge colorScheme="brand" borderRadius="full" px={3} py={0.5} fontSize="xs">
                    <Icon as={FaEnvelope} mr={1} />
                    {application.email}
                  </Badge>
                )}
                {application.place && (
                  <Badge colorScheme="teal" borderRadius="full" px={3} py={0.5} fontSize="xs">
                    <Icon as={FaMapMarkerAlt} mr={1} />
                    {application.place}
                  </Badge>
                )}
                {application.job_title && pickLocalized(application.job_title, i18n.language) && (
                  <Badge colorScheme="purple" borderRadius="full" px={3} py={0.5} fontSize="xs">
                    {pickLocalized(application.job_title, i18n.language)}
                  </Badge>
                )}
                {application.created_at && (
                  <Badge colorScheme="accent" color="brand.900" borderRadius="full" px={3} py={0.5} fontSize="xs">
                    <Icon as={FaCalendarAlt} mr={1} />
                    {formatDate(application.created_at, i18n.language)}
                  </Badge>
                )}
              </Flex>
            </Box>
            <Flex gap={2} align="center" flexWrap="wrap">
              <DocumentButton url={application.cv_url} label={t('admin.cv')} onError={handleError} />
              <DocumentButton url={application.cover_letter_url} label={t('admin.coverLetter')} onError={handleError} />
              <DocumentButton url={application.other_documents_url} label={t('admin.otherDocuments')} onError={handleError} />
              <Button
                leftIcon={<FaTrash />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                isLoading={deletingId === application.id}
                onClick={() => handleDelete(application)}
              >
                {''}
              </Button>
            </Flex>
          </Flex>
        ))
      )}
    </Box>
  );
};

export default ApplicationsPanel;
